// Opening, downloading and printing a document's files — the primary PDF or one of its attachments (`fileId`).
// Shared by the context menu (components/ctx/compta.ts), the edit dialog and the preview's file bar.
import { tr2 } from '../i18n/lang';
import * as A from '../data/actions';
import { printBytes, printUrl, saveBytes, saveUrl } from '../desktop/files';
import { openPdf } from './pdf/pdfjs';
import { openBigView } from '@rimocore/shared/ctx/registry';
import { toast } from './ctx/toast';

const fail = (e: unknown) => toast(e instanceof Error && e.message ? e.message : tr2('Datei nicht verfügbar.', 'Fichier indisponible.'), 'warning');

async function fileOf(id: string, fileId?: string) {
  const r = await A.previewUrl(id, fileId);
  return { url: r.url, mime: r.mimeType || 'application/pdf' };
}
/** Big view (RimoPreview); other files (photos …) are downloaded – never opened in a tab (user rule 15.10.2026). */
export async function openFile(id: string, title: string, fileId?: string) {
  try {
    const f = await fileOf(id, fileId);
    // PDF and the e-invoice (XML: RimoPreview's „no preview“ with its download) in the big view.
    // The primary PDF carries the document id: an incoming invoice shows its notes layer (ES, LayeredPreview).
    if (f.mime.includes('pdf') || f.mime.includes('xml')) openBigView({ url: f.url, title, ...(fileId ? {} : { ref: id }) });
    else await saveUrl(f.url, title);
  } catch (e) { fail(e); }
}
export async function downloadFile(id: string, name: string, fileId?: string) {
  try {
    const f = await fileOf(id, fileId);
    if (f.mime.includes('pdf')) await saveBytes(await openPdf(f.url).bytes, name.replace(/\.pdf$/i, '') + '.pdf', { mime: 'application/pdf' });
    else await saveUrl(f.url, name);
  } catch (e) { fail(e); }
}
/**
 * „Drucken“ of an incoming invoice (ES correction 02.11.2026): always with its escompte box / „Payée“ stamp as a new PDF –
 * the original is not changed; a paid one gets its „gedruckt“ mark (Druckübersicht). „Original herunterladen“ = downloadFile.
 */
export async function printAnnotated(id: string) {
  try { await printBytes((await A.annotatedPdf(id)).bytes); void A.markPrinted([id], 'single').catch(() => {}); } catch (e) { fail(e); }
}
export async function downloadAnnotated(id: string) {
  try { const f = await A.annotatedPdf(id); await saveBytes(f.bytes, f.filename, { mime: 'application/pdf' }); } catch (e) { fail(e); }
}
export async function printFile(id: string, name: string, fileId?: string) {
  try { const f = await fileOf(id, fileId); await printUrl(f.url, name + (f.mime.includes('pdf') && !/\.pdf$/i.test(name) ? '.pdf' : '')); } catch (e) { fail(e); }
}

/** A document file as listed by the API (`files[]` of list rows and GET compta/documents/:id). */
export interface DocFileRef { fileId: string; role: string; file?: { originalFilename?: string | null; mimeType?: string | null } | null }
/** Role of the e-invoice from RimoChantiers (Vertrag A, E1: Peppol BIS 3.0 UBL next to the PDF). */
export const EINVOICE_ROLE = 'einvoice';
/** Primary first, then the e-invoice, then attachments in their order; „Dokument“ / „E-Rechnung (XML)“ / „Anhang 1 · name.pdf“. */
export function fileList(files: DocFileRef[] | null | undefined): Array<DocFileRef & { label: string; primary: boolean }> {
  const rank = (f: DocFileRef) => (f.role === 'primary' ? 0 : f.role === EINVOICE_ROLE ? 1 : 2);
  const list = [...(files ?? [])].sort((a, b) => rank(a) - rank(b));
  let n = 0;
  return list.map((f) => {
    const primary = f.role === 'primary';
    const name = f.file?.originalFilename || '';
    const label = primary ? tr2('Dokument', 'Document') : f.role === EINVOICE_ROLE ? tr2('E-Rechnung (XML)', 'Facture électronique (XML)') : tr2('Anhang ', 'Annexe ') + ++n + (name ? ' · ' + name : '');
    return { ...f, primary, label };
  });
}
